import { assertStaffCapability, assertStaffRelationship, } from "./authorization.js";
import { boundedText } from "./bounded-text.js";
import { DomainValidationError } from "./identifiers.js";
import { ianaTimeZone, localDate, todayIn } from "./time-zone.js";
import { checkExpectedVersion, denied, requireReason, violation } from "./transition-support.js";
// Frozen: the database CHECK constraints are generated from these tuples.
export const tripStatuses = Object.freeze([
    "PLANNING",
    "UPCOMING",
    "ACTIVE",
    "COMPLETED",
    "CANCELLED",
]);
/** Terminal trips are immutable: a new journey is a new trip. */
export const tripTerminalStatuses = Object.freeze(["COMPLETED", "CANCELLED"]);
/** Why a customer cancels, from a closed list (CHT-736). Free text is never taken. */
export const tripCancelReasonCodes = Object.freeze([
    "CHANGE_OF_PLANS",
    "FOUND_ALTERNATIVE",
    "TRAVEL_NOT_POSSIBLE",
    "OTHER",
]);
export const tripCreators = Object.freeze(["CUSTOMER", "STAFF"]);
export const tripCommands = Object.freeze([
    "confirm_plan",
    "reopen_planning",
    "start",
    "complete",
    "cancel",
]);
const rule = (id, from, to, command, who = {}) => Object.freeze({
    id,
    from,
    to,
    command,
    customer: who.customer === true,
    staff: true,
    system: Object.freeze([...(who.system ?? [])]),
});
const CLOCK = ["trip-clock"];
/**
 * The eight allowed transitions. The database guard (LF012) allows exactly these (from, to) pairs.
 * A customer cannot cancel a trip that is under way (T8): live arrangements are staff business.
 */
export const tripTransitions = Object.freeze([
    rule("T1", "PLANNING", "UPCOMING", "confirm_plan", { customer: true }),
    rule("T2", "UPCOMING", "PLANNING", "reopen_planning", { customer: true }),
    rule("T3", "PLANNING", "ACTIVE", "start", { system: CLOCK }),
    rule("T4", "UPCOMING", "ACTIVE", "start", { system: CLOCK }),
    rule("T5", "ACTIVE", "COMPLETED", "complete", { system: CLOCK }),
    rule("T6", "PLANNING", "CANCELLED", "cancel", { customer: true }),
    rule("T7", "UPCOMING", "CANCELLED", "cancel", { customer: true }),
    rule("T8", "ACTIVE", "CANCELLED", "cancel"),
]);
export function isAllowedTripTransition(from, to) {
    return tripTransitions.some((row) => row.from === from && row.to === to);
}
const isTerminal = (status) => tripTerminalStatuses.includes(status);
const AUDIT = {
    confirm_plan: ["trip.plan_confirmed", "trip.plan_confirmed_by_staff"],
    reopen_planning: ["trip.planning_reopened", "trip.planning_reopened_by_staff"],
    start: ["trip.started", "trip.started_by_staff"],
    complete: ["trip.completed", "trip.completed_by_staff"],
    cancel: ["trip.cancelled_by_customer", "trip.cancelled"],
};
function hasDates(trip) {
    return (trip.startsOn !== null &&
        trip.endsOn !== null &&
        trip.startTimeZone !== null &&
        trip.endTimeZone !== null);
}
/**
 * Plans one of the eight allowed transitions, or refuses it. Checks run in this order, so the error
 * is deterministic: (1) the actor's standing (a customer must own the trip, staff need `trip.manage`),
 * (2) the pair (`trip.terminal` from a terminal status, else `trip.transition_invalid`; a command
 * that does not belong to the pair is also invalid), (3) the actor against the rule (a customer or
 * the clock where the rule says so; staff need an effective assignment or the supervisor role),
 * (4) `expectedVersion`, (5) the staff reason, then (6) the evidence: dates and destinations, and for
 * the trip clock the local date in the zone the dates are read in. Nothing is written here.
 */
export function planTripTransition(input) {
    const { trip, to, command, actor, now } = input;
    switch (actor.kind) {
        case "customer":
            if (actor.customerId !== trip.customerId)
                throw denied("not_owner");
            break;
        case "staff":
            assertStaffCapability(actor, "trip.manage", input.environment);
            break;
        case "system":
            break;
    }
    if (isTerminal(trip.status))
        throw violation("trip.terminal");
    const matched = tripTransitions.find((row) => row.from === trip.status && row.to === to);
    if (matched === undefined || matched.command !== command) {
        throw violation("trip.transition_invalid");
    }
    switch (actor.kind) {
        case "customer":
            if (!matched.customer)
                throw denied("role_missing");
            break;
        case "system":
            if (!matched.system.includes(actor.actorId))
                throw denied("role_missing");
            break;
        case "staff":
            assertStaffRelationship(actor.authority, actor.relationship, "CUSTOMER_DATA");
            break;
    }
    checkExpectedVersion(actor, input.expectedVersion, trip.version, "trip.version_conflict");
    if (actor.kind === "staff")
        requireReason(input.reason);
    if (command === "confirm_plan") {
        if (!hasDates(trip) || trip.destinationCount < 1)
            throw violation("trip.plan_incomplete");
    }
    if (command === "start") {
        if (!hasDates(trip))
            throw violation("trip.plan_incomplete");
        if (actor.kind === "system" &&
            todayIn(ianaTimeZone(trip.startTimeZone), now) < trip.startsOn) {
            throw violation("trip.not_started_yet");
        }
    }
    if (command === "complete") {
        if (!hasDates(trip))
            throw violation("trip.plan_incomplete");
        if (actor.kind === "system" &&
            todayIn(ianaTimeZone(trip.endTimeZone), now) <= trip.endsOn) {
            throw violation("trip.not_ended_yet");
        }
    }
    const patch = to === "CANCELLED"
        ? { kind: "set_status", status: to, cancelledAt: "now" }
        : to === "COMPLETED"
            ? { kind: "set_status", status: to, completedAt: "now" }
            : { kind: "set_status", status: to };
    const [customerOrSystemAction, staffAction] = AUDIT[command];
    return Object.freeze({
        ruleId: matched.id,
        command,
        from: trip.status,
        to,
        patch,
        auditAction: actor.kind === "staff" ? staffAction : customerOrSystemAction,
    });
}
// --- Creation --------------------------------------------------------------------------------------
export const TRIP_MAX_DESTINATIONS = 20;
const COUNTRY = /^[A-Z]{2}$/;
const fail = (field, rule) => new DomainValidationError(field, rule);
const present = (value) => value !== null && value !== undefined;
function count(field, value, min, max) {
    if (typeof value !== "number" || !Number.isInteger(value) || value < min || value > max) {
        throw fail(field, `must be a whole number from ${min} to ${max}`);
    }
    return value;
}
/**
 * Validates a new trip. Dates are local calendar dates, each read in its own zone (a multi-country
 * trip starts and ends in different zones), so a zone is required with its date and never without.
 * Destination positions are assigned here, never by the caller. Mirrors the database CHECKs.
 */
export function validateTripDraft(input) {
    const title = boundedText("title", input.title, 140);
    const startsOn = present(input.startsOn) ? localDate(input.startsOn, "startsOn") : null;
    const endsOn = present(input.endsOn) ? localDate(input.endsOn, "endsOn") : null;
    const startTimeZone = present(input.startTimeZone)
        ? ianaTimeZone(input.startTimeZone, "startTimeZone")
        : null;
    const endTimeZone = present(input.endTimeZone)
        ? ianaTimeZone(input.endTimeZone, "endTimeZone")
        : null;
    if (endsOn !== null && (startsOn === null || endsOn < startsOn)) {
        throw fail("endsOn", "must not be before the start date");
    }
    if ((startsOn === null) !== (startTimeZone === null)) {
        throw fail("startTimeZone", "is required with the start date and only then");
    }
    if ((endsOn === null) !== (endTimeZone === null)) {
        throw fail("endTimeZone", "is required with the end date and only then");
    }
    let party = null;
    if (present(input.party)) {
        party = {
            adults: count("party.adults", input.party.adults, 1, 50),
            children: count("party.children", input.party.children, 0, 50),
            infants: count("party.infants", input.party.infants, 0, 50),
        };
    }
    const rawDestinations = input.destinations ?? [];
    if (!Array.isArray(rawDestinations) || rawDestinations.length > TRIP_MAX_DESTINATIONS) {
        throw fail("destinations", `must hold at most ${TRIP_MAX_DESTINATIONS} places`);
    }
    const destinations = rawDestinations.map((destination, position) => {
        if (typeof destination.countryCode !== "string" || !COUNTRY.test(destination.countryCode)) {
            throw fail("destinations.countryCode", "must be a two-letter uppercase country code");
        }
        const arrivesOn = present(destination.arrivesOn)
            ? localDate(destination.arrivesOn, "destinations.arrivesOn")
            : null;
        const departsOn = present(destination.departsOn)
            ? localDate(destination.departsOn, "destinations.departsOn")
            : null;
        if (arrivesOn !== null && departsOn !== null && departsOn < arrivesOn) {
            throw fail("destinations.departsOn", "must not be before the arrival date");
        }
        return {
            position,
            placeName: boundedText("destinations.placeName", destination.placeName, 200),
            locality: present(destination.locality)
                ? boundedText("destinations.locality", destination.locality, 100)
                : null,
            region: present(destination.region)
                ? boundedText("destinations.region", destination.region, 100)
                : null,
            countryCode: destination.countryCode,
            timeZone: present(destination.timeZone)
                ? ianaTimeZone(destination.timeZone, "destinations.timeZone")
                : null,
            arrivesOn,
            departsOn,
        };
    });
    return Object.freeze({
        title,
        startsOn,
        endsOn,
        startTimeZone,
        endTimeZone,
        party,
        destinations: Object.freeze(destinations),
    });
}
// --- Edit ------------------------------------------------------------------------------------------
/** Trips whose basics a customer may change. A trip under way is live arrangements; a closed trip is history. */
export const tripEditableStatuses = Object.freeze(["PLANNING", "UPCOMING"]);
/**
 * Plans a customer's edit of a trip's basics (title, dates and zones, party, destinations: the whole draft replaces the old
 * one), or refuses it. Checks run in this order, so the error is deterministic: (1) the customer owns the trip, (2) the trip is
 * not closed (`trip.terminal`), (3) the trip is PLANNING or UPCOMING (`trip.not_editable` for one under way), (4)
 * `expectedVersion` (`trip.version_conflict`: a stale edit is refused, never merged), (5) a trip that is UPCOMING keeps what made
 * it UPCOMING: both dates with their zones and at least one destination (`trip.plan_incomplete`). The draft was validated by
 * `validateTripDraft` before. Nothing is written here. Staff do not edit a customer's trip basics in this phase.
 */
export function planTripEdit(input) {
    const { trip, draft, actor } = input;
    if (actor.kind !== "customer")
        throw denied("role_missing");
    if (actor.customerId !== trip.customerId)
        throw denied("not_owner");
    if (isTerminal(trip.status))
        throw violation("trip.terminal");
    if (!tripEditableStatuses.includes(trip.status)) {
        throw violation("trip.not_editable");
    }
    checkExpectedVersion(actor, input.expectedVersion, trip.version, "trip.version_conflict");
    if (trip.status === "UPCOMING") {
        const complete = draft.startsOn !== null &&
            draft.endsOn !== null &&
            draft.startTimeZone !== null &&
            draft.endTimeZone !== null &&
            draft.destinations.length >= 1;
        if (!complete)
            throw violation("trip.plan_incomplete");
    }
    return Object.freeze({ status: trip.status, auditAction: "trip.edited" });
}
